import { useCallback, useEffect, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CheckCircle2, Copy, Loader2, MessageCircle, RefreshCw, XCircle } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { API_BASE_URL } from "@/lib/config";

interface AgentStatus {
  enabled: boolean;
  whatsAppConfigured: boolean;
  aiConfigured: boolean;
  model: string;
  signatureValidation: boolean;
  webhookAccepting: boolean;
  plainAddressAccepting: boolean;
  appCredentials: boolean;
  callbackUrl: string;
  verifyToken: string;
  helpUrl: string;
  lastInboundAt: string | null;
  last24h: { inbound: number; aiReplies: number; fixedReplies: number; documentsSent: number; handoffs: number };
  limits: { perHour: number; perDay: number; documentsPerDay: number; fixedRepliesPerDay: number; offTopicPerDay: number; aiRepliesPerDayTotal: number };
  phoneHashesPending: number;
}

interface SetupStep { step: string; ok: boolean; detail: string }
interface RegisterResult { ok: boolean; appSubscriptionSkipped: boolean; callbackUrl: string; steps: SetupStep[] }

const STEP_LABELS: Record<string, string> = {
  config: "Configuração do WhatsApp",
  app_subscription: "App inscrito no campo \"messages\"",
  waba_lookup: "Conta do WhatsApp Business (WABA)",
  waba_subscription: "Webhook do número apontado para esta API",
  waba_check: "Conferência na Meta",
};

const authHeaders = (): Record<string, string> => ({
  Authorization: `Bearer ${localStorage.getItem("token") || ""}`,
  "Content-Type": "application/json",
});

function CopyField({ value, testId }: { value: string; testId: string }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* clipboard blocked: the value is still selectable */ }
  };
  return (
    <span className="flex items-center gap-1">
      <code className="break-all text-xs" data-testid={testId}>{value}</code>
      <button type="button" onClick={copy} className="shrink-0 rounded p-1 hover:bg-muted" aria-label="Copiar" title={copied ? "Copiado" : "Copiar"}>
        {copied ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" /> : <Copy className="h-3.5 w-3.5" />}
      </button>
    </span>
  );
}

function Row({ label, ok, children }: { label: string; ok?: boolean; children?: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-4 py-1.5">
      <span className="text-sm text-muted-foreground">{label}</span>
      <span className="flex items-center gap-1.5 text-sm text-right">
        {ok !== undefined && (ok
          ? <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-600" />
          : <XCircle className="h-4 w-4 shrink-0 text-amber-600" />)}
        {children}
      </span>
    </div>
  );
}

/** System admin: state of the WhatsApp assistant and the one-click webhook registration. */
export function WhatsAppAgentCard() {
  const [status, setStatus] = useState<AgentStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [registering, setRegistering] = useState(false);
  const [result, setResult] = useState<RegisterResult | null>(null);
  const { toast } = useToast();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/admin/whatsapp/agent`, { headers: authHeaders() });
      if (res.ok) setStatus(await res.json());
    } catch { /* status stays empty */ }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const register = async () => {
    setRegistering(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/admin/whatsapp/agent/register`, { method: "POST", headers: authHeaders() });
      if (!res.ok) throw new Error(String(res.status));
      const data: RegisterResult = await res.json();
      setResult(data);
      toast({
        title: data.ok ? "Webhook registrado na Meta" : "Registro incompleto",
        description: data.ok ? "O número já entrega as mensagens para esta API." : "Veja os passos no card.",
        variant: data.ok ? undefined : "destructive",
      });
      load();
    } catch {
      toast({ title: "Erro ao registrar o webhook", variant: "destructive" });
    } finally {
      setRegistering(false);
    }
  };

  const s = status;
  return (
    <Card className="mb-4" data-testid="card-whatsapp-agent">
      <CardHeader>
        <div className="flex items-start justify-between gap-4">
          <div className="space-y-1.5 min-w-0">
            <CardTitle className="flex flex-wrap items-center gap-2 text-base">
              <MessageCircle className="h-4 w-4" /> Assistente do WhatsApp
              {s && <Badge variant={s.enabled && s.whatsAppConfigured && s.aiConfigured && s.webhookAccepting ? "default" : "secondary"}>
                {s.enabled ? (s.whatsAppConfigured && s.aiConfigured && s.webhookAccepting ? "Ativo" : "Incompleto") : "Desligado"}
              </Badge>}
            </CardTitle>
            <CardDescription>
              Responde quem escreve para o número do FillSign: dúvidas, link do documento assinado e link de assinatura, só dos documentos
              enviados para o número de quem pergunta. Número sem documento, assunto fora do FillSign ou mensagens demais recebem resposta
              fixa com a Central de Ajuda, sem gastar IA.
            </CardDescription>
          </div>
          <Button variant="ghost" size="icon" onClick={load} disabled={loading} aria-label="Atualizar" data-testid="whatsapp-agent-refresh">
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {!s ? (
          <p className="text-sm text-muted-foreground">{loading ? "Carregando..." : "Não foi possível ler o estado do assistente."}</p>
        ) : (
          <>
            <div className="divide-y rounded-lg border px-4 py-1">
              <Row label="WhatsApp Cloud API (token e número)" ok={s.whatsAppConfigured}>{s.whatsAppConfigured ? "Configurado" : "Falta configurar"}</Row>
              <Row label="IA (Anthropic)" ok={s.aiConfigured}>{s.aiConfigured ? s.model : "Sem Anthropic__ApiKey"}</Row>
              <Row label="Segurança das entregas" ok={s.webhookAccepting}>
                {s.signatureValidation ? "Assinatura validada (App Secret)" : s.plainAddressAccepting ? "Sem assinatura (só local)" : "Endereço secreto (sem App Secret)"}
              </Row>
              <Row label="Endereço do webhook (Callback URL)">
                <CopyField value={s.callbackUrl} testId="whatsapp-agent-callback" />
              </Row>
              <Row label="Token de verificação (Verify token)">
                <CopyField value={s.verifyToken} testId="whatsapp-agent-verify-token" />
              </Row>
              <Row label="Última mensagem recebida" ok={!!s.lastInboundAt}>
                {s.lastInboundAt ? new Date(s.lastInboundAt).toLocaleString("pt-BR") : "Nenhuma ainda"}
              </Row>
              <Row label="Últimas 24 h">
                {s.last24h.inbound} recebidas · {s.last24h.aiReplies} respostas IA · {s.last24h.fixedReplies} fixas · {s.last24h.documentsSent} envios · {s.last24h.handoffs} atendimentos
              </Row>
              <Row label="Limites por número">
                {s.limits.perHour}/h e {s.limits.perDay}/dia turnos de IA · {s.limits.documentsPerDay} envios/dia · {s.limits.offTopicPerDay} fora de assunto/dia
              </Row>
              {s.phoneHashesPending > 0 && (
                <Row label="Telefones de signatários a indexar">{s.phoneHashesPending} (o worker indexa sozinho)</Row>
              )}
            </div>

            <div className="space-y-2 rounded-lg border bg-muted/40 p-3 text-sm" data-testid="whatsapp-agent-howto">
              <p className="font-medium">Como ligar (uma vez, no painel da Meta)</p>
              <ol className="list-decimal space-y-1 pl-5 text-xs text-muted-foreground">
                <li>
                  Abra <a className="underline" href="https://developers.facebook.com/apps" target="_blank" rel="noopener noreferrer">developers.facebook.com/apps</a>,
                  entre no app do FillSign e vá em <b>WhatsApp → Configuração</b> (Configuration).
                </li>
                <li>
                  Em <b>Webhook</b>, clique em <b>Editar</b>: cole o <b>endereço do webhook</b> em "URL de callback" e o <b>token de verificação</b> em
                  "Token de verificação". Clique em <b>Verificar e salvar</b>.
                </li>
                <li>Em <b>Campos do webhook</b>, clique em <b>Gerenciar</b> e assine o campo <b>messages</b>.</li>
                <li>Volte aqui e clique em <b>Registrar webhook na Meta</b> (aponta o número para este ambiente; pode repetir).</li>
              </ol>
              <p className="text-xs text-muted-foreground">
                O endereço termina com um token secreto, por isso funciona sem o App Secret. Com WhatsApp__Cloud__AppSecret configurado,
                a assinatura de cada entrega também é validada. Um único número só entrega para um ambiente por vez: o último registrado.
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <Button onClick={register} disabled={registering || !s.whatsAppConfigured} data-testid="whatsapp-agent-register">
                {registering && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Registrar webhook na Meta
              </Button>
              <span className="text-xs text-muted-foreground">
                Aponta as mensagens recebidas pelo número para o endereço acima. Pode repetir sem problema.
              </span>
            </div>

            {result && (
              <div className="space-y-1 rounded-lg border p-3" data-testid="whatsapp-agent-steps">
                {result.steps.map((step) => {
                  const skipped = !step.ok && step.step === "app_subscription" && result.appSubscriptionSkipped;
                  return (
                    <div key={step.step} className="flex items-start gap-2 text-sm">
                      {step.ok
                        ? <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
                        : <XCircle className={`mt-0.5 h-4 w-4 shrink-0 ${skipped ? "text-amber-600" : "text-destructive"}`} />}
                      <div className="min-w-0">
                        <p className="font-medium">{STEP_LABELS[step.step] ?? step.step}</p>
                        <p className="break-all text-xs text-muted-foreground">{step.detail}</p>
                      </div>
                    </div>
                  );
                })}
                {result.appSubscriptionSkipped && (
                  <p className="pt-2 text-xs text-muted-foreground">
                    Sem WhatsApp__Cloud__AppId e WhatsApp__Cloud__AppSecret no servidor, o passo 2 e 3 acima (webhook do app e campo
                    "messages") precisam estar feitos no painel da Meta.
                  </p>
                )}
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
